<template>
  <div class="layout">
    <router-view v-if="route.path === '/login'" class="full-page" />
    <template v-else>
      <div class="layout-header">
        <div class="layout-header--logo" @click="router.push('/')">
          后台管理
        </div>

        <div class="layout-header--main">
          <el-icon @click="isCollapse = !isCollapse">
            <Menu />
          </el-icon>
        </div>

        <!-- 右侧工具栏 -->
        <div style="padding-right: 20px; gap: 20px; display: flex; cursor: pointer">
          <!-- 音频标签（静音预加载） -->
          <audio ref="audioRecharge" :src="rechargeAudio" preload="auto" muted />
          <!-- <audio ref="audioWithdraw" :src="withdrawAudio" preload="auto" muted /> -->

          <!-- 声音开关 -->
          <div @click="toggleMute" :title="isMuted ? '打开声音' : '关闭声音'" style="width: 24px; height: 24px">
            <svg v-if="!isMuted" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 1-4.5 4.5v-9a4.5 4.5 0 0 1 4.5 4.5z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white">
              <path d="M16.5 12a4.5 4.5 0 0 0-4.5-4.5v9a4.5 4.5 0 0 0 4.5-4.5zm1.91 5.59L4.41 4.09 3 5.5l4.47 4.47H3v6h4l5 5V13.9l5.5 5.5 1.41-1.41z"/>
            </svg>
          </div>

          <!-- 统计信息 -->
          <el-badge title="在线人数" :value="onlineCount" @click="router.push('/userList')" class="item">
            <el-icon><User /></el-icon>
          </el-badge>
          <el-badge title="实名" :value="notice.idcard" @click="router.push('/realnameList')" class="item">
            <el-icon><Comment /></el-icon>
          </el-badge>
          <el-badge title="充值" :value="notice.recharge" @click="router.push('/chargeList')" class="item">
            <el-icon><Wallet /></el-icon>
          </el-badge>
          <el-badge title="提款" :value="notice.withdraw" @click="router.push('/withdrawList')" class="item">
            <el-icon><CreditCard /></el-icon>
          </el-badge>
          <el-badge title="转账" :value="notice.transferCount" @click="router.push('/transferLogLists')" class="item">
            <el-icon><CreditCard /></el-icon>
          </el-badge>
        </div>

        <!-- 用户信息 -->
        <el-dropdown placement="bottom-start">
          <el-button>
            用户： {{ store.state.currentUser?.username || currentUser?.username || '未登录' }}
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <!-- 主体 -->
      <div class="layout-body">
        <leftMenu :isCollapse="isCollapse" />
        <div class="layout-main">
          <el-breadcrumb class="layout-main--breadcrumb" separator-icon="ArrowRight">
            <el-breadcrumb-item v-for="(item, index) in store.state.breakCum" :to="{ path: item.path }">
              {{ item.name }}
            </el-breadcrumb-item>
          </el-breadcrumb>
          <router-view class="layout-main--routerview" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch, onActivated } from "vue";
import leftMenu from "./leftMenu.vue";
import store from "@/store";
import { useRoute, useRouter } from "vue-router";
import rechargeAudio from "@/assets/video/recharge.mp3";
import withdrawAudio from "@/assets/video/cash.mp3";

const isCollapse = ref(false);
const route = useRoute();
const router = useRouter();

let currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "null");
onActivated(() => {
  currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "null");
});

// 声音相关
const audioRecharge = ref(null);
// const audioWithdraw = ref(null);
const isMuted = ref(false); // 默认开启声音

// 播放队列与节流
const playQueue = [];
let isPlaying = false;
const MIN_PLAY_GAP_MS = 8000; // 两次播报最小间隔，避免过快重复
let lastPlayTime = 0;

// 入队播放
const enqueuePlay = (audioRef) => {
  const now = Date.now();
  // 若刚播报完且间隔未到，直接忽略此次请求
  if (now - lastPlayTime < MIN_PLAY_GAP_MS) return;
  playQueue.push(audioRef);
  playNext();
};

const playNext = () => {
  if (isPlaying || playQueue.length === 0) return;

  const audio = playQueue.shift();
  if (!isMuted.value) {
    isPlaying = true;
    audio.muted = false;
    audio.currentTime = 0;
    audio.play().then(() => {
      audio.onended = () => {
        lastPlayTime = Date.now();
        isPlaying = false;
        playNext();
      };
    }).catch((err) => {
      console.warn("播放失败:", err);
      lastPlayTime = Date.now();
      isPlaying = false;
      playNext();
    });
  }
};

// 播放方法
const playRecharge = () => enqueuePlay(audioRecharge.value);
// const playWithdraw = () => enqueuePlay(audioWithdraw.value);

// 播放模式持久化
const playMode = ref(localStorage.getItem("playMode") || "always");
watch(playMode, (val) => localStorage.setItem("playMode", val));

// 统计信息
const notice = ref({ recharge: 0, withdraw: 0, idcard: 0 });
const onlineCount = ref(0);
const { proxy } = getCurrentInstance();

// 声音开关
const toggleMute = () => {
  isMuted.value = !isMuted.value;

  // 如果刚解锁，触发一次静音预播来解锁 Chrome 限制
  if (!isMuted.value) {
    [audioRecharge.value].forEach(a => {
      a.muted = true;
      a.play().then(() => {
        a.pause();
        a.currentTime = 0;
        a.muted = false;
      }).catch(() => {});
    });
  }
};

// 请求接口
const getOnlineCount = async () => {
  try {
    const res = await proxy.$http({ url: "/index/onlineUser" });
    if (res.code == 0) {
      onlineCount.value = res.data.count;
    }
  } catch (error) {
    console.log(error);
  }
};

const getNotice = async () => {
  try {
    const res = await proxy.$http({ url: "/index/notice" });
    if (res.code === 0) {
      if (playMode.value === "always") {
        if (res.data.recharge > 0) playRecharge();
        // if (res.data.withdraw > 0) playWithdraw();
      } else if (playMode.value === "change") {
        if (res.data.recharge > notice.value.recharge) playRecharge();
        // if (res.data.withdraw > notice.value.withdraw) playWithdraw();
      }
      notice.value = res.data;
    }
  } catch (error) {
    console.error(error);
  }
};

// 生命周期
onMounted(() => {
  // 预加载资源（静音）
  audioRecharge.value.play().then(() => {
    audioRecharge.value.pause();
    audioRecharge.value.currentTime = 0;
  }).catch(() => {});

  // audioWithdraw.value.play().then(() => {
  //   audioWithdraw.value.pause();
  //   audioWithdraw.value.currentTime = 0;
  // }).catch(() => {});

  getNotice();
  setInterval(getNotice, 30000);
  getOnlineCount();
});

// 登出
const logout = () => {
  sessionStorage.removeItem("__TOKEN");
  currentUser = null;
  router.push("/login");
};
</script>

<style lang="less" scoped>
.layout {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &-header {
    background-color: #222;
    color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 44px;
    padding: 0 12px;

    &--logo {
      width: 220px;
      text-align: left;
      cursor: pointer;
    }

    &--main {
      flex: 1;
      width: 100%;
      color: #fff;
      text-align: left;

      .el-icon {
        cursor: pointer;
      }
    }
  }

  &-body {
    flex: 1;
    height: 100%;
    overflow: hidden;
    display: flex;
  }

  &-main {
    width: 100%;
    flex: 1;
    height: 100%;
    overflow: auto;
    background-color: #f1f1f1;
    display: flex;
    flex-direction: column;

    &--breadcrumb {
      height: 44px;
      display: flex;
      width: 100%;
      background-color: #fff;
      padding: 0 12px;
    }

    &--routerview {
      display: flex;
      flex: 1;
      height: 100%;
      margin: 12px;
      padding: 12px;
      overflow: auto;
      background-color: #fff;
      border-radius: 3px;
    }
  }
}
</style>
